import { createContext, ReactNode, useCallback, useMemo, useState } from 'react'
import {
  validateFormData,
  ValidationConfig,
  ValidationRule,
} from './helpers/validate-fields'

interface ValidationContextType {
  tabs: number[]
  error: Record<string, ValidationRule>
  clearFieldError: (name: string, tabNumber?: number) => void
  validateForm: <T extends object>(
    formData: T,
    config: ValidationConfig,
    fieldToTabMap?: Record<string, number>,
  ) => {
    fieldErrors: Record<string, ValidationRule<unknown>>
    tabErrors: Record<number, string[]>
  }
  validateFormAccumulate: <T extends object>(
    formData: T,
    config: ValidationConfig,
    fieldToTabMap?: Record<string, number>,
  ) => {
    fieldErrors: Record<string, ValidationRule<unknown>>
    tabErrors: Record<number, string[]>
  }
  clearErrorsByPrefix: (prefix: string) => void
  clearErrorsByPaths: (paths: string[]) => void
  clearErrorsByObjectId: (objectId: string, basePath?: string) => void
  addManualError: (
    fieldPath: string,
    rule: ValidationRule,
    tabNumber?: number,
  ) => void
  removeManualError: (fieldPath: string) => void
  clearAllErrors: () => void
  clearTabError: (tabNumber: number) => void
  markTabWithError: (tabNumber: number, message?: string) => void
}

interface Props {
  children: ReactNode
}

const defaultContext: ValidationContextType = {
  tabs: [],
  error: {},
  clearFieldError: () => {},
  validateForm: () => ({
    fieldErrors: {},
    tabErrors: {},
  }),
  validateFormAccumulate: () => ({
    fieldErrors: {},
    tabErrors: {},
  }),
  clearErrorsByPrefix: () => {},
  clearErrorsByPaths: () => {},
  clearErrorsByObjectId: () => {},
  addManualError: () => {},
  removeManualError: () => {},
  clearAllErrors: () => {},
  markTabWithError: () => {},
  clearTabError: () => {},
}

export const TabsValidationContext =
  createContext<ValidationContextType>(defaultContext)

export const TabsValidationProvider = ({ children }: Props) => {
  const [tabs, setTabs] = useState<Record<number, string[]>>({})
  const [error, setError] = useState<Record<string, ValidationRule>>({})

  const clearFieldError = useCallback(
    (name: string, tabNumber?: number) => {
      setError((prev) => {
        const newError = { ...prev }
        delete newError[name]
        return newError
      })

      if (tabNumber !== undefined && tabs[tabNumber]) {
        setTabs((prev) => {
          const newTabs = { ...prev }
          newTabs[tabNumber] = newTabs[tabNumber].filter(
            (field) => field !== name,
          )
          if (newTabs[tabNumber].length === 0) {
            delete newTabs[tabNumber]
          }
          return newTabs
        })
      }
    },
    [tabs],
  )

  const validateForm = useCallback(
    <T extends object>(
      formData: T,
      config: ValidationConfig,
      fieldToTabMap?: Record<string, number>,
    ) => {
      const { fieldErrors, tabErrors } = validateFormData(
        formData,
        config,
        fieldToTabMap,
      )
      setError(fieldErrors)
      setTabs(tabErrors)

      return { fieldErrors, tabErrors }
    },
    [],
  )

  const validateFormAccumulate = useCallback(
    <T extends object>(
      formData: T,
      config: ValidationConfig,
      fieldToTabMap?: Record<string, number>,
    ) => {
      const { fieldErrors, tabErrors } = validateFormData(
        formData,
        config,
        fieldToTabMap,
      )

      setError((prev) => ({
        ...prev,
        ...fieldErrors,
      }))

      setTabs((prev) => {
        const newTabs = { ...prev }
        Object.entries(tabErrors).forEach(([tabNum, fields]) => {
          const tabNumber = Number(tabNum)
          const existingFields = new Set(newTabs[tabNumber] || [])
          fields.forEach((field) => existingFields.add(field))
          newTabs[tabNumber] = Array.from(existingFields)
        })
        return newTabs
      })

      return { fieldErrors, tabErrors }
    },
    [],
  )

  const clearErrorsByPrefix = useCallback((prefix: string) => {
    setError((prev) => {
      const newErrors = { ...prev }
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(prefix)) {
          delete newErrors[key]
        }
      })
      return newErrors
    })

    setTabs((prev) => {
      const newTabs = { ...prev }
      Object.keys(newTabs).forEach((tabNum) => {
        const tabNumber = Number(tabNum)
        newTabs[tabNumber] = newTabs[tabNumber].filter(
          (field) => !field.startsWith(prefix),
        )
        if (newTabs[tabNumber].length === 0) {
          delete newTabs[tabNumber]
        }
      })
      return newTabs
    })
  }, [])

  const clearErrorsByPaths = useCallback((paths: string[]) => {
    setError((prev) => {
      const newErrors = { ...prev }
      paths.forEach((path) => {
        delete newErrors[path]
      })
      return newErrors
    })

    setTabs((prev) => {
      const newTabs = { ...prev }
      Object.keys(newTabs).forEach((tabNum) => {
        const tabNumber = Number(tabNum)
        newTabs[tabNumber] = newTabs[tabNumber].filter(
          (field) => !paths.includes(field),
        )
        if (newTabs[tabNumber].length === 0) {
          delete newTabs[tabNumber]
        }
      })
      return newTabs
    })
  }, [])

  const clearErrorsByObjectId = useCallback(
    (objectId: string, basePath = '') => {
      const fullPrefix = basePath ? `${basePath}.${objectId}` : objectId

      setError((prev) => {
        const newErrors = { ...prev }
        Object.keys(newErrors).forEach((key) => {
          if (key.includes(fullPrefix)) {
            delete newErrors[key]
          }
        })
        return newErrors
      })

      setTabs((prev) => {
        const newTabs = { ...prev }
        Object.keys(newTabs).forEach((tabNum) => {
          const tabNumber = Number(tabNum)
          newTabs[tabNumber] = newTabs[tabNumber].filter(
            (field) => !field.includes(fullPrefix),
          )
          if (newTabs[tabNumber].length === 0) {
            delete newTabs[tabNumber]
          }
        })
        return newTabs
      })
    },
    [],
  )

  const addManualError = useCallback(
    (fieldPath: string, rule: ValidationRule, tabNumber?: number) => {
      setError((prev) => ({
        ...prev,
        [fieldPath]: rule,
      }))

      if (tabNumber !== undefined) {
        setTabs((prev) => {
          const newTabs = { ...prev }
          newTabs[tabNumber] = newTabs[tabNumber] || []
          if (!newTabs[tabNumber].includes(fieldPath)) {
            newTabs[tabNumber].push(fieldPath)
          }
          return newTabs
        })
      }
    },
    [],
  )

  const removeManualError = useCallback((fieldPath: string) => {
    setError((prev) => {
      const newErrors = { ...prev }
      delete newErrors[fieldPath]
      return newErrors
    })
  }, [])

  const clearAllErrors = useCallback(() => {
    setError({})
    setTabs({})
  }, [])

  const markTabWithError = useCallback(
    (tabNumber: number, message?: string) => {
      addManualError(
        `__tab_${tabNumber}__`,
        {
          textError: message,
          required: false,
        },
        tabNumber,
      )
    },
    [addManualError],
  )

  const clearTabError = useCallback(
    (tabNumber: number) => {
      clearFieldError(`__tab_${tabNumber}__`, tabNumber)
    },
    [clearFieldError],
  )

  const contextValue = useMemo(
    () => ({
      tabs: Object.keys(tabs).map(Number),
      error,
      validateForm,
      validateFormAccumulate,
      clearFieldError,
      clearErrorsByPrefix,
      clearErrorsByPaths,
      clearErrorsByObjectId,
      addManualError,
      removeManualError,
      clearAllErrors,
      markTabWithError,
      clearTabError,
    }),
    [
      tabs,
      error,
      validateForm,
      validateFormAccumulate,
      clearFieldError,
      clearErrorsByPrefix,
      clearErrorsByPaths,
      clearErrorsByObjectId,
      addManualError,
      removeManualError,
      clearAllErrors,
      markTabWithError,
      clearTabError,
    ],
  )

  return (
    <TabsValidationContext.Provider value={contextValue}>
      {children}
    </TabsValidationContext.Provider>
  )
}
